/**
 * @fileoverview Song Library Route
 *
 * Preset song library page for browsing and copying songs to personal flashcard decks.
 * Features two-panel layout with search on the right and song details on the left.
 *
 * **Layout:**
 * - Left Panel (60%): Selected song details, lyrics, audio player, copy button
 * - Right Panel (40%): Search input, mode toggle, filtered song list
 *
 * **Features:**
 * - Fuzzy search with Fuse.js (Spanish+English, Spanish-only, Metadata modes)
 * - Audio playback for songs with audio files
 * - Copy entire song to user's flashcard deck
 * - Responsive mobile layout (stacks vertically)
 *
 * @module routes/app/songs/library
 */

import { createFileRoute } from '@tanstack/react-router'
import { useQuery } from 'convex/react'
import { api } from '../../../../convex/_generated/api'
import { useState } from 'react'
import { SongSearchPanel } from '@/components/SongSearchPanel'
import { SongDetailPanel, SongDetailPanelEmpty } from '@/components/SongDetailPanel'
import { Helmet } from 'react-helmet-async'

export const Route = createFileRoute('/app/songs/library')({
  component: SongLibraryPage,
})

function SongLibraryPage() {
  const [selectedSongId, setSelectedSongId] = useState<string | null>(null)

  // Fetch all preset songs
  const songsResult = useQuery(api.songs.list, {})
  const rawSongs = songsResult?.page || []
  const isLoading = rawSongs.length === 0 && !songsResult

  // Deduplicate songs by title (keep first occurrence)
  const songs = rawSongs.reduce((acc, song) => {
    const isDuplicate = acc.some((s) => s.title === song.title)
    if (!isDuplicate) {
      acc.push(song)
    }
    return acc
  }, [] as typeof rawSongs)

  // Get selected song details
  const selectedSong = selectedSongId ? songs.find((s) => s._id === selectedSongId) : null

  return (
    <>
      <Helmet>
        <title>Song Library | JingLearn</title>
        <meta
          name="description"
          content="Browse and search Spanish learning songs. Copy songs to your flashcard deck for vocabulary practice."
        />
      </Helmet>

      <div className="flex flex-col h-screen lg:flex-row bg-black">
        {/* Left Panel - Song Details (60%) */}
        <div className="flex-1 lg:w-3/5 border-b lg:border-b-0 lg:border-r border-gray-700 overflow-hidden">
          {selectedSong ? (
            <SongDetailPanel song={selectedSong} />
          ) : (
            <SongDetailPanelEmpty />
          )}
        </div>

        {/* Right Panel - Search & Song List (40%) */}
        <div className="lg:w-2/5 h-1/2 lg:h-full">
          <SongSearchPanel
            songs={songs}
            selectedSongId={selectedSongId || undefined}
            onSelectSong={setSelectedSongId}
            isLoading={isLoading}
          />
        </div>
      </div>
    </>
  )
}
